import React, { ForwardRefRenderFunction, forwardRef } from "react";
//#region types
import { IntegrationFormTypes, SettingsFormTypes } from "models/formTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { BillogramFormContainerProps } from "models/propTypes/integrationsPropTypes";
import { FormFieldConstants } from "utils/constants";
//#endregion
//#region components
import BillogramForm from "./billogramForm";
import { Skeletons } from "components/common";
import { useSettingsService } from "serviceHooks";
//#endregion
import * as Yup from "yup";

const BillogramFormContainer: ForwardRefRenderFunction<FormHandles, BillogramFormContainerProps> = (props, ref) => {
  const settingsService = useSettingsService();
  const [projectDimensions, setProjectDimensions] = React.useState<WintSuperkollAPIModel.DimensionItem[]>([]);
  const [costCenterDimensions, setCostCenterDimensions] = React.useState<WintSuperkollAPIModel.DimensionItem[]>([]);

  const [loading, setLoading] = React.useState<boolean>(true);

  const appData = props.appData as WintSuperkollAPIModel.Billogram;

  const validationSchema = Yup.object<IntegrationFormTypes.Billogram>({
    billogramAuthUser: Yup.string().when(
      FormFieldConstants.Integration.Billogram.UseBillogram,
      ([useBillogram], schema: Yup.StringSchema) =>
        useBillogram ? schema.required(TranslationKeys.Validations.Required) : schema
    ),
    billogramAuthKey: Yup.string().when(
      FormFieldConstants.Integration.Billogram.UseBillogram,
      ([useBillogram], schema: Yup.StringSchema) =>
        useBillogram ? schema.required(TranslationKeys.Validations.Required) : schema
    ),
    useBillogram: Yup.boolean(),
    useBillogramDescription: Yup.string(),
    billogramAccountMappingListItems: Yup.array().of(
      Yup.object<IntegrationFormTypes.BillogramAccountMappingListItem>({
        accountNumber: Yup.number().required(TranslationKeys.Validations.Required),
        constCenterName: Yup.string(),
        costCenterId: Yup.string(),
        projectName: Yup.string(),
        projectId: Yup.string(),
      })
    ),
  });

  const defaultValues: IntegrationFormTypes.Billogram = {
    // eslint-disable-next-line eqeqeq --- disabling to avoid regression testing if changed
    useBillogram: props.isActive == true || props.activationState != WintSuperkollAPIModel.AppActivationState.Inactive,
    billogramAuthUser: appData.billogramAuthUser,
    billogramAuthKey: appData.billogramAuthKey,
    billogramAccountMappingListItems:
      appData.billogramAccountMappingList?.map((item) => ({
        id: item.id,
        accountNumber: item.accountNumber,
        costCenterId: item.dimensions?.find((d) => costCenterDimensions.some((c) => c.id === d.dimensionId))
          ?.dimensionId,
        constCenterName: item.dimensions?.find((d) => costCenterDimensions.some((c) => c.id === d.dimensionId))?.name,
        projectId: item.dimensions?.find((d) => projectDimensions.some((c) => c.id === d.dimensionId))?.dimensionId,
        projectName: item.dimensions?.find((d) => projectDimensions.some((c) => c.id === d.dimensionId))?.name,
      })) ?? [],
  };

  const getSubmitDimensions = (item: IntegrationFormTypes.BillogramAccountMappingListItem) => {
    const items = [];
    item.costCenterId && items.push({ dimensionId: item.costCenterId, name: item.constCenterName });
    item.projectId && items.push({ dimensionId: item.projectId, name: item.projectName });
    return items;
  };

  const handleSubmit = async (values: IntegrationFormTypes.Billogram) => {
    const billogram = {
      billogramAuthUser: values.useBillogram ? values.billogramAuthUser : "",
      billogramAuthKey: values.useBillogram ? values.billogramAuthKey : "",
      isActive: values.useBillogram,
      billogramAccountMappingList:
        values.useBillogram &&
        values.billogramAccountMappingListItems &&
        values.billogramAccountMappingListItems.length > 0
          ? values.billogramAccountMappingListItems?.map(
              (item) =>
                ({
                  id: item.id,
                  accountNumber: item.accountNumber,
                  dimensions: getSubmitDimensions(item),
                } as WintSuperkollAPIModel.BillogramAccountingList)
            )
          : null,
    } as WintSuperkollAPIModel.BillogramPut;

    await settingsService.updateBillogram(billogram);

    return (
      props.submitResultFormatter &&
      props.submitResultFormatter({
        ...props,
        isActive: props.isActive && values.useBillogram,
        activationState: values.useBillogram
          ? // eslint-disable-next-line eqeqeq --- disabling to avoid regression testing if changed
            props.activationState == WintSuperkollAPIModel.AppActivationState.Inactive
            ? WintSuperkollAPIModel.AppActivationState.Waiting
            : props.activationState
          : WintSuperkollAPIModel.AppActivationState.Inactive,
        appData: { ...appData, ...billogram },
      } as SettingsFormTypes.IntegrationItem)
    );
  };

  React.useEffect(() => {
    //TODO: Find a proper way to retrive dimensions
    props.onFormValidationChange && props.onFormValidationChange(false);
    settingsService.getDimensionSettings().then((res) => {
      const projectDims = res.dimensionSettingItems?.find(
        (i) => i.dimensionType === WintSuperkollAPIModel.DimensionCompatibilityType.Project
      )?.dimensionList;
      const costCenterDims = res.dimensionSettingItems?.find(
        (i) => i.dimensionType === WintSuperkollAPIModel.DimensionCompatibilityType.CostCenter
      )?.dimensionList;

      projectDims && setProjectDimensions(projectDims);
      costCenterDims && setCostCenterDimensions(costCenterDims);
      setLoading(false);
      props.onFormValidationChange && props.onFormValidationChange(true);
    });
  }, []);

  return loading ? (
    <Skeletons.FormSkeleton></Skeletons.FormSkeleton>
  ) : (
    <BillogramForm
      ref={ref}
      {...props}
      validationSchema={validationSchema}
      defaultValues={defaultValues}
      handleSubmit={handleSubmit}
      projectDimensions={projectDimensions}
      costCenterDimensions={costCenterDimensions}
    />
  );
};

export default forwardRef(BillogramFormContainer);
